/**
 * Token store — single source of truth for the access/refresh tokens the SPA
 * holds in the *password-login* flow.
 *
 * Storage choice: sessionStorage (cleared when the tab closes) + an in-memory
 * mirror for synchronous reads. We deliberately avoid localStorage (persists
 * across sessions, larger XSS blast radius).
 *
 * NOTE on the SSO/cookie flow: the OAuth callback delivers tokens as HttpOnly
 * cookies that JavaScript cannot read, so this store stays EMPTY for SSO logins.
 * API auth still works there via `credentials: "include"` (cookies are sent
 * cross-subdomain on the `.<env>.oxbo.energy` cookie domain).
 */

const ACCESS_KEY = "oxbo_admin_access_token";
const REFRESH_KEY = "oxbo_admin_refresh_token";

let accessToken: string | null = null;
let refreshToken: string | null = null;
let hydrated = false;

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  try {
    accessToken = sessionStorage.getItem(ACCESS_KEY);
    refreshToken = sessionStorage.getItem(REFRESH_KEY);
  } catch {
    // sessionStorage unavailable (e.g. SSR/locked-down) — stay in-memory only.
  }
}

export const tokenStore = {
  /** Synchronous access-token read (used by the OxboAuthClient config). */
  getAccessToken(): string | null {
    hydrate();
    return accessToken;
  },

  getRefreshToken(): string | null {
    hydrate();
    return refreshToken;
  },

  setTokens(access: string, refresh?: string | null): void {
    hydrate();
    accessToken = access;
    if (refresh !== undefined) refreshToken = refresh;
    try {
      sessionStorage.setItem(ACCESS_KEY, access);
      if (refresh) sessionStorage.setItem(REFRESH_KEY, refresh);
    } catch {
      /* in-memory only */
    }
  },

  clear(): void {
    hydrate();
    accessToken = null;
    refreshToken = null;
    try {
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
  },
};
